/**
 * Call hierarchy
 *
 * Three flavors of items, distinguished by `data.kind`:
 *
 *   - module:    incoming = modules that depend on it,
 *                outgoing = modules it depends on
 *                (powered by the dependency graph)
 *
 *   - interface: incoming = process steps targeting this interface
 *                outgoing = (none — interfaces don't call out)
 *
 *   - process:   incoming = (none — processes aren't called)
 *                outgoing = every distinct interface it calls
 *
 *   - subprocess: incoming = every `do <sub>` site that invokes it
 *                 outgoing = every distinct callee it reaches (like a process)
 */

import {
  SymbolKind as LspSymbolKind,
  type CallHierarchyIncomingCall,
  type CallHierarchyItem,
  type CallHierarchyOutgoingCall,
  type Range,
} from "vscode-languageserver";
import {
  getDependencies,
  getDependents,
  forEachCallHop,
  forEachNode,
  type Flow,
  type SourceSpan,
} from "@archlang/engine";
import type { CursorContext } from "../workspace/cursor-context.js";
import type { SymbolEntry, SymbolTable } from "../workspace/symbol-table.js";
import type { Workspace } from "../workspace/workspace.js";
import { resolveSymbolAtCursor } from "../workspace/resolve.js";
import { toLspRange } from "../lsp-types.js";

type ItemKind = "module" | "surface" | "interface" | "process" | "subprocess";

interface ItemData {
  qualifiedName: string;
  kind: ItemKind;
}

export function prepareCallHierarchy(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  uri: string,
  line: number,
  character: number
): CallHierarchyItem[] {
  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym) return [];
  if (
    sym.kind !== "module" &&
    sym.kind !== "surface" &&
    sym.kind !== "interface" &&
    sym.kind !== "process" &&
    sym.kind !== "subprocess"
  ) {
    return [];
  }
  const item = symbolToItem(sym);
  return item ? [item] : [];
}

export function incomingCalls(
  workspace: Workspace,
  symbols: SymbolTable,
  item: CallHierarchyItem
): CallHierarchyIncomingCall[] {
  const data = (item.data as ItemData | undefined) ?? {
    qualifiedName: item.name,
    kind: "module" as ItemKind,
  };

  switch (data.kind) {
    case "module":
    case "surface": {
      const graph = workspace.dependencyGraph();
      if (!graph) return [];
      const target = graph.modulesByPath.get(data.qualifiedName);
      if (!target) return [];
      const callers = getDependents(graph, target);
      return callers
        .map((mod) => moduleToItem(symbols, mod.name, item.uri))
        .filter((it): it is CallHierarchyItem => it !== null)
        .map((from) => ({ from, fromRanges: [from.range] }));
    }
    case "interface": {
      // Find every process step whose `to` resolves to this interface.
      const out: CallHierarchyIncomingCall[] = [];
      const model = workspace.model();
      const target = data.qualifiedName;

      // Process steps (from the IR)
      for (const pm of model.processModels ?? []) {
        const sites = collectCallSitesCalling(pm.body, target);
        if (sites.length === 0) continue;
        const procItem = symbolToItem({
          id: `process:${pm.name}`,
          kind: "process",
          name: pm.name,
          qualifiedName: pm.name,
          definingUri: undefined,
          declSpan: pm.source,
          nameSpan: pm.nameSource,
          typeName: "process",
        } as SymbolEntry);
        if (!procItem) continue;
        out.push({ from: procItem, fromRanges: sites.map((s) => toLspRange(s)) });
      }

      return out;
    }
    case "process":
      // Processes aren't called from elsewhere in the model.
      return [];
    case "subprocess": {
      // A subprocess IS called — every `do <sub>` site (in any process or other
      // subprocess) is an incoming caller.
      const out: CallHierarchyIncomingCall[] = [];
      const model = workspace.model();
      const name = data.qualifiedName.split(".").pop()!;
      const callers: {
        readonly callerName: string;
        readonly kind: ItemKind;
        readonly body: Flow;
        readonly declSpan: SourceSpan | undefined;
        readonly nameSpan: SourceSpan | undefined;
      }[] = [
        ...(model.processModels ?? []).map((p) => ({ callerName: p.name, kind: "process" as ItemKind, body: p.body, declSpan: p.source, nameSpan: p.nameSource })),
        ...(model.subprocessModels ?? [])
          .filter((sp) => sp.name !== name) // self-recursion isn't a "caller"
          .map((sp) => ({ callerName: sp.name, kind: "subprocess" as ItemKind, body: sp.body, declSpan: sp.source, nameSpan: sp.nameSource })),
      ];
      for (const c of callers) {
        const sites = collectDoSitesTargeting(c.body, name);
        if (sites.length === 0) continue;
        const item = symbolToItem({
          id: `${c.kind}:${c.callerName}`,
          kind: c.kind,
          name: c.callerName,
          qualifiedName: c.callerName,
          definingUri: undefined,
          declSpan: c.declSpan,
          nameSpan: c.nameSpan,
          typeName: c.kind,
        } as SymbolEntry);
        if (!item) continue;
        out.push({ from: item, fromRanges: sites.map((s) => toLspRange(s)) });
      }
      return out;
    }
  }
}

export function outgoingCalls(
  workspace: Workspace,
  symbols: SymbolTable,
  item: CallHierarchyItem
): CallHierarchyOutgoingCall[] {
  const data = (item.data as ItemData | undefined) ?? {
    qualifiedName: item.name,
    kind: "module" as ItemKind,
  };

  switch (data.kind) {
    case "module":
    case "surface": {
      const graph = workspace.dependencyGraph();
      if (!graph) return [];
      const source = graph.modulesByPath.get(data.qualifiedName);
      if (!source) return [];
      const callees = getDependencies(graph, source);
      return callees
        .map((mod) => moduleToItem(symbols, mod.name, item.uri))
        .filter((it): it is CallHierarchyItem => it !== null)
        .map((to) => ({ to, fromRanges: [to.range] }));
    }
    case "interface":
      // Interfaces have no outgoing calls — events are reached by normal
      // process `>` edges, not declared on the interface.
      return [];
    case "process": {
      // Prefer the process declared in the ITEM's own file (v0.13): twin
      // processes across spaces share the bare name.
      const procs = (workspace.model().processModels ?? []).filter(
        (p) => p.name === data.qualifiedName,
      );
      const proc = procs.find((p) => p.source?.file === item.uri) ?? procs[0];
      if (!proc) return [];
      const targets = new Set<string>();
      collectCallTargets(proc.body, targets);
      const out: CallHierarchyOutgoingCall[] = [];
      for (const qname of targets) {
        // Referrer-aware: the process's callees resolve in ITS space.
        const sym = symbols.resolve(qname, proc.source?.file);
        if (!sym) continue;
        const ifaceItem = symbolToItem(sym);
        if (!ifaceItem) continue;
        out.push({ to: ifaceItem, fromRanges: [ifaceItem.range] });
      }
      return out;
    }
    case "subprocess": {
      // A subprocess calls out exactly like a process — every distinct callee
      // (interfaces + nested `do` targets) it reaches.
      const name = data.qualifiedName.split(".").pop()!;
      const subs = (workspace.model().subprocessModels ?? []).filter(
        (s) => s.name === name || `${s.ownerPath?.join(".") ?? ""}.${s.name}`.replace(/^\./, "") === data.qualifiedName,
      );
      const sub = subs.find((s) => s.source?.file === item.uri) ?? subs[0];
      if (!sub) return [];
      const targets = new Set<string>();
      collectCallTargets(sub.body, targets);
      const out: CallHierarchyOutgoingCall[] = [];
      for (const qname of targets) {
        const sym = symbols.resolve(qname, sub.source?.file);
        if (!sym) continue;
        const item2 = symbolToItem(sym);
        if (!item2) continue;
        out.push({ to: item2, fromRanges: [item2.range] });
      }
      return out;
    }
  }
}

// =============================================================================
// Helpers
// =============================================================================

function symbolToItem(sym: SymbolEntry): CallHierarchyItem | null {
  const span = sym.nameSpan ?? sym.declSpan;
  if (!span) return null;
  const r: Range = toLspRange(span);
  const lspKind =
    sym.kind === "module"
      ? LspSymbolKind.Module
      : sym.kind === "surface"
      ? LspSymbolKind.Class
      : sym.kind === "interface"
      ? LspSymbolKind.Method
      : LspSymbolKind.Function;
  return {
    name: sym.name,
    detail: sym.typeName,
    kind: lspKind,
    uri: sym.definingUri ?? "",
    range: r,
    selectionRange: r,
    data: {
      qualifiedName: sym.qualifiedName,
      kind: sym.kind as ItemKind,
    } satisfies ItemData,
  };
}

function moduleToItem(
  symbols: SymbolTable,
  qualifiedName: string,
  fromUri?: string
): CallHierarchyItem | null {
  // Referrer-aware (v0.13): a twin module name resolves near the item's file.
  const sym = symbols.resolve(qualifiedName, fromUri);
  return sym ? symbolToItem(sym) : null;
}

/** Callee spans (per hop) in the IR body whose written callee path == `target`
 *  — the "where this interface is called" sites. */
function collectCallSitesCalling(body: Flow | undefined, target: string): SourceSpan[] {
  const out: SourceSpan[] = [];
  forEachCallHop(body, (hop) => {
    const to = hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath;
    if (to.join(".") === target && hop.callee.text.source) out.push(hop.callee.text.source);
  });
  return out;
}

/** `do <name>` target spans (recursing all containers) whose target's last
 *  segment equals `name` — the call sites that invoke a given subprocess. */
function collectDoSitesTargeting(body: Flow | undefined, name: string): SourceSpan[] {
  const out: SourceSpan[] = [];
  forEachNode(body, (n) => {
    if (n.kind === "splice" && (n.target[n.target.length - 1] ?? "") === name) {
      const s = n.targetSource ?? n.source;
      if (s) out.push(s);
    }
  });
  return out;
}

/** Every distinct callee qname a process/subprocess reaches: written callee
 *  paths (per hop) + `do` targets. */
function collectCallTargets(body: Flow | undefined, out: Set<string>): void {
  forEachCallHop(body, (hop) => {
    const to = hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath;
    out.add(to.join("."));
  });
  forEachNode(body, (n) => {
    if (n.kind === "splice") out.add(n.target.join("."));
  });
}
